/**
 * The `<tc-radio-group>` vocabulary: its attributes, its part types and the
 * `data-tc-part` names it stores. The authoring plugin and the runtime live
 * in `@tripcart/page-builder`, the tag table and the runtime manifest here,
 * so the one copy lives here — the same reason `combobox-settings.ts` does.
 *
 * A radio group is a PART, not a block: it is stored inside the block that
 * uses it, which also decides each radio's `name` and `value` and which one
 * starts checked. It is never offered as a tile or to the copilot
 * (`INTERACTIVE_PART_TAGS`).
 *
 * NATIVE RADIOS, SO NO BEHAVIOUR OF OUR OWN. The browser already does what
 * the APG radio group asks for: one tab stop, arrow keys that move and
 * check, a `change` event that bubbles, form submission and form reset. The
 * runtime only installs the sliding active background
 * (`radio-group-sheet.ts` in `@tripcart/page-builder`).
 */
export const RADIO_GROUP_TYPE = "tc-radio-group";

/**
 * Attributes on the `<tc-radio-group>` root.
 *
 * `variant` is the look: `segmented`, a pill track with a solid active
 * segment (a year toggle), or `chips`, separate outlined pills with a tinted
 * active one (a "Sort by" row), or `tabs`, a row of words over one rule
 * with the active one underlined (a month row), or `tiles`, a scrolling row
 * of small boxes, each a word over a quiet line (a month and how many dates
 * it holds).
 *
 * `label-display="hidden"` keeps the group's name for a screen reader and
 * takes it off the screen, for a group whose options already say what it is.
 */
export const RADIO_GROUP_ATTRS = {
  variant: "variant",
  labelDisplay: "label-display",
} as const;

export const RADIO_GROUP_VARIANTS = [
  "segmented",
  "chips",
  "tabs",
  "tiles",
] as const;
export type RadioGroupVariant = (typeof RADIO_GROUP_VARIANTS)[number];
export const DEFAULT_RADIO_GROUP_VARIANT: RadioGroupVariant = "segmented";

/**
 * The `data-tc-part` names of the group's children.
 *
 * `group` is the `<fieldset>` and `label` its `<legend>`, which names the
 * group with no ids to keep in step. `options` holds the choices; it is also
 * what the active background slides inside. `option` is one `<label>`,
 * holding its `input` (the radio, visually hidden) and its `text`.
 */
export const RADIO_GROUP_PARTS = {
  group: "group",
  label: "label",
  options: "options",
  option: "option",
  input: "input",
  text: "text",
} as const;
export type RadioGroupPart =
  (typeof RADIO_GROUP_PARTS)[keyof typeof RADIO_GROUP_PARTS];

/**
 * The GrapesJS type of each part. Only the root is a custom element: every
 * part renders as the native element that already behaves right
 * (`COMPONENT_TYPE_TAGS` in `component-element.ts`).
 */
export const RADIO_GROUP_PART_TYPES = {
  group: "tc-radio-group-group",
  label: "tc-radio-group-label",
  options: "tc-radio-group-options",
  option: "tc-radio-group-option",
  input: "tc-radio-group-input",
  text: "tc-radio-group-text",
} as const satisfies Record<RadioGroupPart, string>;
